/**
 * Iter 67bb — Vendor-side platform billing card.
 *
 * Shown inside the Payouts tab. Lists every platform invoice the vendor has
 * received (subscription, commission, refund-debt, ad-hoc) and highlights
 * the outstanding total so the vendor knows what they owe the platform.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { FileText, Receipt } from "lucide-react";
import Money from "@/components/Money";
import InvoiceDeliveryActions from "@/components/invoices/InvoiceDeliveryActions";

const STATUS_TONES = {
  issued:    "bg-blue-50 text-blue-700",
  overdue:   "bg-amber-50 text-amber-800",
  paid:      "bg-emerald-50 text-emerald-700",
  cancelled: "bg-rose-50 text-rose-700",
};

const KIND_LABELS = {
  subscription: "Subscription",
  commission:   "Commission",
  refund_debt:  "Refund debt",
  adhoc:        "Other",
};

export default function VendorPlatformInvoicesCard() {
  const [items, setItems] = useState([]);
  const [outstanding, setOutstanding] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/vendor/platform-invoices?limit=50")
      .then((r) => {
        setItems(r.data.items || []);
        setOutstanding(r.data.total_outstanding || 0);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden" data-testid="vendor-platform-invoices-card">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-lg bg-indigo-50 ring-1 ring-indigo-100 flex items-center justify-center">
            <Receipt className="h-3.5 w-3.5 text-indigo-700" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900 text-sm">Platform invoices</div>
            <div className="text-xs text-neutral-500">{items.length} issued by the platform</div>
          </div>
        </div>
        {outstanding > 0 && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-900">Outstanding</div>
            <Money amount={outstanding} className="font-display text-lg font-semibold text-amber-900" />
          </div>
        )}
      </div>
      <div className="divide-y divide-border">
        {items.map((inv) => (
          <div key={inv.id} className="px-5 py-3 flex items-center justify-between gap-4" data-testid={`vendor-platform-invoice-${inv.id}`}>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-neutral-900 text-sm flex items-center gap-2 flex-wrap">
                <FileText className="h-3.5 w-3.5 text-neutral-500" />
                {inv.invoice_number}
                <Badge variant="outline" className={`uppercase tracking-wider text-[10px] font-bold ${STATUS_TONES[inv.status] || STATUS_TONES.issued}`}>{inv.status}</Badge>
                {inv.ad_hoc && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 ring-1 ring-violet-200">Ad-hoc</span>}
              </div>
              <div className="text-xs text-neutral-500 mt-0.5 truncate">
                {(inv.line_items || []).map((li) => KIND_LABELS[li.kind] || "Other").join(" · ") || "Platform billing"}
                {" · "}
                Issued {inv.issued_at?.slice(0, 10) || "—"}
                {inv.due_at && <> · Due {inv.due_at.slice(0, 10)}</>}
              </div>
            </div>
            <div className="text-right shrink-0 flex items-center gap-3">
              <Money amount={inv.totals?.total_gross || 0} className="font-semibold text-neutral-900" />
              <InvoiceDeliveryActions
                kind="platform"
                invoiceId={inv.id}
                invoiceNumber={inv.invoice_number}
                variant="icons"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
